import {useEffect, useRef, useState} from "react";
import {ReferralOfferPopover} from "download/recipient/ReferralOfferDisclosure";
import {GameStage} from "games/GameStage";
import {PanelButton} from "games/GameFrame.styles";
import {useMessages} from "i18n";
import type {RecipientArcade, RecipientArcadeStage} from "./useRecipientArcade";
import {
	ArcadeGameColumn, FinishRow, GiftButton, KeepPlayingButton, PopoverHost, QuietButton,
} from "./RecipientArcadeGame.styles";

/**
 * The recipient arcade's open game, in the column beside the file card, over the sender's art
 * (kitecorp/transferify-ui#2889, kitecorp/transferify-ui#2990). The file card is never covered, and
 * closing the game changes nothing about the download.
 */
export function RecipientArcadeGame({arcade}: {arcade: RecipientArcade}) {
	if (!arcade.open || arcade.game === null) {
		return null;
	}
	const {stage} = arcade;
	return (
		<ArcadeGameColumn data-testid="recipient-arcade-game">
			<GameStage
				key={stage.round}
				game={arcade.game}
				mode="download"
				progress={stage.progress}
				endless={stage.endless}
				statusLine={stage.statusLine}
				finishActions={<RecipientFinishActions stage={stage}/>}
				onClose={arcade.close}
				onContinue={arcade.close}
				onRunFinished={stage.onRunFinished}
				onRunClosed={stage.onRunClosed}
			/>
		</ArcadeGameColumn>
	);
}

/**
 * The finish screen's buttons, once the download completed during the game: send your own files, the
 * quiet account or gift button where the page allows it (`finishActions`), and free play.
 */
function RecipientFinishActions({stage}: {stage: RecipientArcadeStage}) {
	const copy = useMessages().games.recipient;
	const [giftAnchor, setGiftAnchor] = useState<HTMLElement | null>(null);
	const shown = useRef(false);
	const {onFinishShown} = stage;
	useEffect(() => {
		if (!shown.current) {
			shown.current = true;
			onFinishShown();
		}
	}, [onFinishShown]);
	const {send, secondary} = stage.finish;
	return (
		<>
			{(send || secondary !== null) && (
				<FinishRow>
					{send && <PanelButton type="button" onClick={stage.onSend}>{copy.send}</PanelButton>}
					{secondary === "account" && (
						<QuietButton type="button" aria-haspopup="dialog" onClick={event => stage.onCreateAccount(event.currentTarget)}>
							{copy.createAccount}
						</QuietButton>
					)}
					{secondary === "gift" && stage.offer !== null && (
						<GiftButton type="button" aria-haspopup="dialog" aria-expanded={giftAnchor !== null}
							onClick={event => {
								setGiftAnchor(event.currentTarget);
								stage.onGiftOpened();
							}}>
							{copy.claimGift}
						</GiftButton>
					)}
				</FinishRow>
			)}
			<KeepPlayingButton type="button" onClick={stage.onKeepPlaying}>{copy.keepPlaying}</KeepPlayingButton>
			{stage.offer !== null && (
				<PopoverHost onPointerDown={event => event.stopPropagation()}>
					<ReferralOfferPopover offer={stage.offer} senderName={stage.senderName} anchor={giftAnchor}
						onClose={() => setGiftAnchor(null)} onClaimed={stage.onGiftClaimed}/>
				</PopoverHost>
			)}
		</>
	);
}
